import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, FileSignature, FileText, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { orderReviewsAPI } from '../../services/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const STATUS_META = {
  pending: ['Sheet not created', 'bg-amber-100 text-amber-800'],
  draft: ['In progress', 'bg-blue-100 text-blue-800'],
  downloaded: ['Awaiting signed copy', 'bg-violet-100 text-violet-800'],
};

const FILTERS = [
  { key: 'all', label: 'All unsigned', countKey: 'total_unsigned', icon: ClipboardCheck },
  { key: 'pending', label: 'Sheet not created', countKey: 'no_sheet', icon: FileText },
  { key: 'draft', label: 'In progress', countKey: 'in_progress', icon: Loader2 },
  { key: 'downloaded', label: 'Awaiting signed copy', countKey: 'awaiting_signed', icon: FileSignature },
];

const dueClass = (days) => {
  if (days === null || days === undefined) return 'text-slate-400';
  if (days < 0) return 'text-rose-600 font-semibold';
  if (days <= 7) return 'text-amber-600 font-semibold';
  return 'text-slate-700';
};

const dueLabel = (days) => {
  if (days === null || days === undefined) return 'no date';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'due today';
  return `in ${days}d`;
};

export default function ReviewWorklistPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await orderReviewsAPI.worklist({ include_completed: includeCompleted });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the review worklist');
    } finally { setRefreshing(false); }
  }, [includeCompleted]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const items = data?.items || [];
    const term = search.trim().toLowerCase();
    return items.filter((item) => (filter === 'all' || item.review_status === filter)
      && (!term || [item.order_number, item.customer_name, item.customer_po_number, item.review_sheet_no]
        .some((value) => (value || '').toLowerCase().includes(term))));
  }, [data, filter, search]);

  if (!data) {
    return <div className="flex min-h-[400px] items-center justify-center" data-testid="review-worklist-loading"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="review-worklist-page">
      <header className="flex flex-col gap-3 border-b pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Order review worklist</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every live sales order whose Order Review &amp; Feasibility Sheet (F-MKT-04) is not yet signed —
            sorted by the earliest customer delivery date. {data.summary.signed} sheet(s) already signed are hidden.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setIncludeCompleted((value) => !value)} data-testid="worklist-include-completed">
            {includeCompleted ? 'Live orders only' : 'Include completed / cancelled'}
          </Button>
          <Button variant="outline" onClick={load} disabled={refreshing} data-testid="worklist-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {FILTERS.map((item) => {
          const active = filter === item.key;
          const Icon = item.icon;
          return (
            <button key={item.key} type="button" onClick={() => setFilter(item.key)} aria-pressed={active}
              className={`rounded-xl border px-4 py-3 text-left transition-colors duration-150 ${
                active ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
              data-testid={`worklist-filter-${item.key}`}>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                <Icon className="h-3.5 w-3.5" /> {item.label}
              </div>
              <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-slate-900">
                {data.summary[item.countKey]}
              </p>
            </button>
          );
        })}
      </div>

      <Input value={search} onChange={(event) => setSearch(event.target.value)} className="max-w-sm"
        placeholder="Search order no., customer, PO no. or sheet no." data-testid="worklist-search" />

      <div className="overflow-x-auto border bg-white">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr className="border-b bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Order date</th>
              <th className="px-3 py-2">Delivery date</th>
              <th className="px-3 py-2 text-right">Order value</th>
              <th className="px-3 py-2">Review sheet</th>
              <th className="px-3 py-2">Last activity</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody data-testid="worklist-table-body">
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="worklist-empty">
                Nothing pending here — every matching order has a signed review sheet.
              </td></tr>
            ) : rows.map((row) => {
              const [label, className] = STATUS_META[row.review_status] || STATUS_META.pending;
              return (
                <tr key={row.order_id} className="border-b transition-colors duration-150 last:border-0 hover:bg-blue-50/40"
                  data-testid={`worklist-row-${row.order_number}`}>
                  <td className="px-3 py-2">
                    <button type="button" className="font-mono text-xs font-semibold text-blue-700 underline"
                      onClick={() => navigate(`/orders/${row.order_id}`)}>{row.order_number}</button>
                    <span className="block text-[11px] text-slate-500">
                      {row.customer_po_number ? `PO ${row.customer_po_number}` : 'no PO no.'} · {row.order_status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2">{row.customer_name}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {row.order_date}
                    {row.order_age_days !== null && <span className="block text-[11px] text-slate-400">{row.order_age_days}d ago</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={dueClass(row.days_to_delivery)}>{row.delivery_date || '—'}</span>
                    <span className="block text-[11px] text-slate-400">{dueLabel(row.days_to_delivery)}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.order_value ? `${row.currency} ${Number(row.order_value).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={className}>{label}</Badge>
                    {row.review_sheet_no && <span className="mt-1 block font-mono text-[11px] text-slate-500">{row.review_sheet_no}</span>}
                    {row.outcome && <span className="block text-[11px] text-slate-500">{row.outcome.replace(/_/g, ' ')}</span>}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {row.last_activity_at
                      ? `${new Date(row.last_activity_at).toLocaleDateString('en-IN')}${row.last_activity_by ? ` · ${row.last_activity_by}` : ''}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant={row.review_status === 'pending' ? 'default' : 'outline'}
                      onClick={() => navigate(`/orders/${row.order_id}/review-sheet`)}
                      data-testid={`worklist-open-${row.order_number}`}>
                      {row.review_status === 'pending' ? 'Create sheet' : 'Open sheet'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
